import React from 'react';
import {
  Box,
  Skeleton,
  Card,
  CardContent,
  Grid,
  Paper,
} from '@mui/material';

function DashboardSkeleton() {
  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      {/* Page Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center' }}>
        <Skeleton
          variant="rounded"
          width={56}
          height={56}
          sx={{
            borderRadius: 2,
            mr: 3,
          }}
        />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width={320} height={48} sx={{ mb: 1 }} />
          <Skeleton variant="text" width={480} height={28} />
        </Box>
      </Box>

      {/* Macro Summary Chart */}
      <Card
        elevation={0}
        sx={{
          mb: 3,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Skeleton variant="text" width={240} height={32} sx={{ mb: 0.5 }} />
              <Skeleton variant="text" width={360} height={24} />
            </Box>
            <Skeleton variant="rounded" width={120} height={40} />
          </Box>
          <Box sx={{ height: 300, position: 'relative' }}>
            <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 2 }} />
          </Box>
        </CardContent>
      </Card>

      {/* Macro Score Trend */}
      <Card
        elevation={0}
        sx={{
          mb: 3,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Skeleton variant="text" width={200} height={32} sx={{ mb: 3 }} />
          <Box sx={{ height: 250 }}>
            <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 2 }} />
          </Box>
        </CardContent>
      </Card>

      {/* AI News Impact Dashboard */}
      <Card
        elevation={0}
        sx={{
          mb: 3,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Skeleton variant="circular" width={40} height={40} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width={280} height={32} />
              <Skeleton variant="text" width={400} height={24} />
            </Box>
          </Box>
          <Grid container spacing={2}>
            {[1, 2, 3].map((i) => (
              <Grid item xs={12} md={4} key={i}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                  }}
                >
                  <Skeleton variant="text" width="60%" height={24} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width={100} height={48} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width="80%" height={20} />
                </Paper>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Risk Management Widget */}
      <Card
        elevation={0}
        sx={{
          mb: 3,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Skeleton variant="text" width={200} height={32} sx={{ mb: 3 }} />
          <Grid container spacing={2}>
            {[1, 2, 3, 4].map((i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Box sx={{ textAlign: 'center' }}>
                  <Skeleton variant="circular" width={120} height={120} sx={{ mx: 'auto', mb: 2 }} />
                  <Skeleton variant="text" width="80%" height={24} sx={{ mx: 'auto' }} />
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Top Investment Opportunities */}
      <Box sx={{ mb: 3 }}>
        <Skeleton variant="text" width={280} height={36} sx={{ mb: 2 }} />
        <Grid container spacing={3}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} md={4} key={i}>
              <Card
                elevation={0}
                sx={{
                  border: '2px solid',
                  borderColor: 'divider',
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Skeleton variant="text" width="60%" height={32} />
                    <Skeleton variant="rounded" width={40} height={32} sx={{ borderRadius: 4 }} />
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Skeleton variant="text" width="40%" height={24} sx={{ mb: 1 }} />
                    <Skeleton variant="text" width={80} height={48} />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <Skeleton variant="rounded" width="48%" height={40} />
                    <Skeleton variant="rounded" width="48%" height={40} />
                  </Box>
                  <Skeleton variant="rounded" width="100%" height={36} sx={{ borderRadius: 2 }} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Risk & Breadth Section */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Skeleton variant="text" width={180} height={32} sx={{ mb: 3 }} />
              <Box sx={{ height: 200 }}>
                <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 2 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Skeleton variant="text" width={180} height={32} sx={{ mb: 3 }} />
              <Box sx={{ height: 200 }}>
                <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 2 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Sector Momentum */}
      <Box sx={{ mb: 3 }}>
        <Skeleton variant="text" width={220} height={36} sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <Skeleton variant="text" width="70%" height={24} />
                  <Skeleton variant="circular" width={32} height={32} />
                </Box>
                <Skeleton variant="text" width="50%" height={36} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="60%" height={20} />
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Market Insights Chart */}
      <Card
        elevation={0}
        sx={{
          mb: 3,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Skeleton variant="text" width={200} height={32} sx={{ mb: 3 }} />
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[1, 2, 3, 4].map((i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Box sx={{ textAlign: 'center', p: 2 }}>
                  <Skeleton variant="text" width="60%" height={20} sx={{ mx: 'auto', mb: 1 }} />
                  <Skeleton variant="text" width={80} height={40} sx={{ mx: 'auto' }} />
                </Box>
              </Grid>
            ))}
          </Grid>
          <Box sx={{ height: 300 }}>
            <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 2 }} />
          </Box>
        </CardContent>
      </Card>

      {/* Charts Section */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Skeleton variant="text" width={200} height={32} sx={{ mb: 3 }} />
              <Box sx={{ height: 350 }}>
                <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 2 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Skeleton variant="text" width={200} height={32} sx={{ mb: 3 }} />
              <Box sx={{ height: 350 }}>
                <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 2 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Market Forecast */}
      <Card
        elevation={0}
        sx={{
          mb: 3,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Skeleton variant="text" width={220} height={32} sx={{ mb: 3 }} />
          <Box sx={{ height: 300 }}>
            <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 2 }} />
          </Box>
        </CardContent>
      </Card>

      {/* Advanced Analytics Section */}
      <Box sx={{ mt: 4, mb: 2 }}>
        <Skeleton variant="text" width={280} height={36} sx={{ mb: 1 }} />
        <Skeleton variant="text" width={420} height={24} sx={{ mb: 3 }} />
      </Box>

      {/* Advanced Charts Grid */}
      <Grid container spacing={3}>
        {[1, 2, 3, 4].map((i) => (
          <Grid item xs={12} md={6} key={i}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Skeleton variant="text" width={240} height={32} sx={{ mb: 3 }} />
                <Box sx={{ height: 320 }}>
                  <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 2 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Animated shimmer effect */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: 'linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.5), transparent)',
          animation: 'shimmer 2s infinite',
          zIndex: 9999,
          '@keyframes shimmer': {
            '0%': { transform: 'translateX(-100%)' },
            '100%': { transform: 'translateX(100%)' },
          },
        }}
      />
    </Box>
  );
}

export default DashboardSkeleton;

